import React, { useState, useEffect } from 'react'
import { Card, CardContent, CardHeader } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Badge } from '@/components/ui/badge'
import { Switch } from '@/components/ui/switch'
import { Checkbox } from '@/components/ui/checkbox'
import { Skeleton } from '@/components/ui/skeleton'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  Search,
  Gamepad2,
  Server,
  Layers,
  ChevronLeft,
  ChevronRight,
  RefreshCcw,
  Power,
  PowerOff,
  Eye,
  Image as ImageIcon,
  Flame,
  Sparkles,
  Wifi,
  WifiOff,
  Wrench,
  Download,
  Loader2,
  CheckCircle,
  XCircle,
} from 'lucide-react'
import { cn } from '@/lib/utils'
import { GameService, Game, GameCategory, GameProvider, GameStatus } from '@/domain/game'
import { toast } from 'sonner'
import { getCurrentParseConfig } from '@/services/adapters/parse/parse-client'
import { ConfirmDialog } from '@/components/ui/confirm-dialog'

type TabType = 'games' | 'categories' | 'providers'

// ============================================
// Loading Skeleton
// ============================================
function TableSkeleton({ rows = 5 }: { rows?: number }) {
  return (
    <div className="space-y-3 p-4">
      {Array.from({ length: rows }).map((_, i) => (
        <div key={i} className="flex items-center gap-4">
          <Skeleton className="h-4 w-4" />
          <Skeleton className="h-12 w-12 rounded-lg" />
          <div className="flex-1 space-y-2">
            <Skeleton className="h-4 w-48" />
            <Skeleton className="h-3 w-32" />
          </div>
          <Skeleton className="h-6 w-16 rounded-full" />
          <Skeleton className="h-6 w-10" />
        </div>
      ))}
    </div>
  )
}

// ============================================
// Game Detail Dialog
// ============================================
function GameDetailDialog({ 
  game, 
  open, 
  onOpenChange 
}: { 
  game: Game | null
  open: boolean
  onOpenChange: (open: boolean) => void 
}) {
  if (!game) return null
  
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-3">
            <div className="h-12 w-12 rounded-xl bg-gradient-to-br from-primary/20 to-primary/5 flex items-center justify-center">
              {game.image_vertical ? (
                <img src={game.image_vertical} alt={game.gameName} className="h-10 w-10 object-cover rounded-lg" />
              ) : (
                <Gamepad2 className="h-6 w-6 text-primary" />
              )}
            </div>
            <div>
              <div>{game.gameName}</div>
              {game.gameNameTh && game.gameNameTh !== game.gameName && (
                <div className="text-sm font-normal text-muted-foreground">{game.gameNameTh}</div>
              )}
            </div>
          </DialogTitle>
          <DialogDescription>รายละเอียดเกม</DialogDescription>
        </DialogHeader>
        
        <div className="space-y-4 py-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="p-3 bg-muted/50 rounded-lg">
              <div className="text-xs text-muted-foreground mb-1">Game ID</div>
              <div className="font-mono text-sm">{game.game_id || game.id}</div>
            </div>
            <div className="p-3 bg-muted/50 rounded-lg">
              <div className="text-xs text-muted-foreground mb-1">สถานะ</div>
              <Badge className={cn(
                "border-0",
                game.status === 'ACTIVE' ? "bg-emerald-100 text-emerald-700" : "bg-gray-100 text-gray-700"
              )}>
                {game.status === 'ACTIVE' ? 'เปิดใช้งาน' : 'ปิดใช้งาน'}
              </Badge>
            </div>
            <div className="p-3 bg-muted/50 rounded-lg">
              <div className="text-xs text-muted-foreground mb-1">ประเภท</div>
              <div className="font-medium">{game.game_Category || '-'}</div>
            </div>
            <div className="p-3 bg-muted/50 rounded-lg">
              <div className="text-xs text-muted-foreground mb-1">ผู้ให้บริการ</div>
              <div className="font-medium">{game.provider || '-'}</div>
            </div>
          </div>
          
          <div className="flex items-center gap-2">
            {game.isHot && (
              <Badge className="gap-1 bg-orange-100 text-orange-700 border-0">
                <Flame className="h-3 w-3" /> HOT
              </Badge>
            )}
            {game.isNew && (
              <Badge className="gap-1 bg-blue-100 text-blue-700 border-0">
                <Sparkles className="h-3 w-3" /> NEW
              </Badge>
            )}
          </div>
          
          <div className="text-xs text-muted-foreground">
            สร้างเมื่อ: {new Date(game.createdAt).toLocaleString('th-TH')}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  )
}

// ============================================
// Main Component
// ============================================
export function GamesListPage() {
  const [activeTab, setActiveTab] = useState<TabType>('games')
  const [searchTerm, setSearchTerm] = useState('')
  const [isLoading, setIsLoading] = useState(true)
  const [isRefreshing, setIsRefreshing] = useState(false)
  
  // Sync Game State
  const [isSyncing, setIsSyncing] = useState(false)
  const [syncResult, setSyncResult] = useState<{
    success: boolean
    message: string
    data?: any
  } | null>(null)
  const [showSyncDialog, setShowSyncDialog] = useState(false)
  const [showSyncConfirm, setShowSyncConfirm] = useState(false)
  
  // Data
  const [games, setGames] = useState<Game[]>([])
  const [categories, setCategories] = useState<GameCategory[]>([])
  const [providers, setProviders] = useState<GameProvider[]>([])
  const [gamesTotal, setGamesTotal] = useState(0)
  
  // Selection
  const [selectedGames, setSelectedGames] = useState<string[]>([])
  const [selectedCategories, setSelectedCategories] = useState<string[]>([])
  const [selectedProviders, setSelectedProviders] = useState<string[]>([])
  
  // Filters
  const [filterCategory, setFilterCategory] = useState('all')
  const [filterProvider, setFilterProvider] = useState('all')
  const [filterStatus, setFilterStatus] = useState<'all' | GameStatus>('all')
  
  // Pagination
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(20)
  
  // Detail dialog
  const [selectedGame, setSelectedGame] = useState<Game | null>(null)
  const [showDetail, setShowDetail] = useState(false)

  // ============================================
  // Load Data
  // ============================================
  useEffect(() => {
    loadData()
  }, [activeTab, page, pageSize, filterCategory, filterProvider, filterStatus])
  
  // Debounce search
  useEffect(() => {
    const timer = setTimeout(() => {
      if (activeTab === 'games') {
        loadGames()
      } else if (activeTab === 'categories') {
        loadCategories()
      } else {
        loadProviders()
      }
    }, 300)
    return () => clearTimeout(timer)
  }, [searchTerm])
  
  const loadData = async () => {
    setIsLoading(true)
    try {
      // Load all in parallel
      await Promise.all([
        loadGames(),
        loadCategories(),
        loadProviders(),
      ])
    } finally {
      setIsLoading(false)
    }
  }
  
  const loadGames = async () => {
    try {
      const result = await GameService.findAllGames({
        query: searchTerm || undefined,
        category: filterCategory !== 'all' ? filterCategory : undefined,
        provider: filterProvider !== 'all' ? filterProvider : undefined,
        status: filterStatus !== 'all' ? filterStatus : undefined,
        page,
        limit: pageSize,
      })
      setGames(result.items)
      setGamesTotal(result.total)
    } catch (error) {
      console.error('Error loading games:', error)
      toast.error('ไม่สามารถโหลดข้อมูลเกมได้')
    }
  }
  
  const loadCategories = async () => {
    try {
      const result = await GameService.findAllCategories({ query: searchTerm || undefined })
      setCategories(result.items)
    } catch (error) {
      console.error('Error loading categories:', error)
    }
  }
  
  const loadProviders = async () => {
    try {
      const result = await GameService.findAllProviders({ query: searchTerm || undefined })
      setProviders(result.items)
    } catch (error) {
      console.error('Error loading providers:', error)
    }
  }
  
  const handleRefresh = async () => {
    setIsRefreshing(true)
    await loadData()
    setIsRefreshing(false)
    toast.success('รีเฟรชข้อมูลสำเร็จ')
  }

  // ============================================
  // Sync Game Handler
  // ============================================
  const handleSyncGames = () => {
    setShowSyncConfirm(true)
  }

  const executeSyncGames = async () => {
    setShowSyncConfirm(false)
    
    try {
      setIsSyncing(true)
      setSyncResult(null)
      setShowSyncDialog(true)

      const config = getCurrentParseConfig()
      const sessionToken = localStorage.getItem('sessionToken')

      const response = await fetch(
        `${config.serverURL}/functions/triggerGameSync`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-Parse-Application-Id': config.appId,
            ...(sessionToken
              ? { 'X-Parse-Session-Token': sessionToken }
              : { 'X-Parse-Master-Key': config.masterKey }),
          },
          body: JSON.stringify({}),
        }
      )

      const result = await response.json()

      if (result.result?.success) {
        setSyncResult({
          success: true,
          message: 'Sync เกมสำเร็จ!',
          data: result.result.data,
        })
        toast.success('Sync เกมสำเร็จ!')
        // Reload data after sync
        await loadData()
      } else {
        setSyncResult({
          success: false,
          message: result.result?.error || result.error || 'เกิดข้อผิดพลาดในการ sync',
        })
        toast.error('Sync เกมล้มเหลว')
      }
    } catch (error: any) {
      setSyncResult({
        success: false,
        message: error.message || 'เกิดข้อผิดพลาด',
      })
      toast.error('เกิดข้อผิดพลาดในการ sync')
    } finally {
      setIsSyncing(false)
    }
  }

  // ============================================
  // Stats
  // ============================================
  const stats = {
    games: { 
      total: gamesTotal, 
      active: games.filter(g => g.status === 'ACTIVE').length 
    },
    categories: { 
      total: categories.length, 
      active: categories.filter(c => c.status === 'ACTIVE').length 
    },
    providers: { 
      total: providers.length, 
      active: providers.filter(p => p.status === 'ACTIVE').length 
    },
  }

  // ============================================
  // Toggle handlers
  // ============================================
  const toggleGame = async (id: string, currentStatus: GameStatus) => {
    const newStatus = currentStatus === 'ACTIVE' ? 'INACTIVE' : 'ACTIVE'
    try {
      await GameService.toggleGameStatus(id, newStatus)
      setGames(prev => prev.map(g => g.id === id ? { ...g, status: newStatus } : g))
      toast.success(`${newStatus === 'ACTIVE' ? 'เปิด' : 'ปิด'}ใช้งานเกมสำเร็จ`)
    } catch (error) {
      toast.error('เกิดข้อผิดพลาด')
    }
  }

  const toggleCategory = async (id: string, currentStatus: GameStatus) => {
    const newStatus = currentStatus === 'ACTIVE' ? 'INACTIVE' : 'ACTIVE'
    try {
      await GameService.toggleCategoryStatus(id, newStatus)
      setCategories(prev => prev.map(c => c.id === id ? { ...c, status: newStatus } : c))
      toast.success(`${newStatus === 'ACTIVE' ? 'เปิด' : 'ปิด'}ใช้งานประเภทสำเร็จ`)
    } catch (error) {
      toast.error('เกิดข้อผิดพลาด')
    }
  }

  const toggleProvider = async (id: string, currentStatus: GameStatus) => {
    const newStatus = currentStatus === 'ACTIVE' ? 'INACTIVE' : 'ACTIVE'
    try {
      await GameService.toggleProviderStatus(id, newStatus)
      setProviders(prev => prev.map(p => p.id === id ? { ...p, status: newStatus } : p))
      toast.success(`${newStatus === 'ACTIVE' ? 'เปิด' : 'ปิด'}ใช้งานผู้ให้บริการสำเร็จ`)
    } catch (error) {
      toast.error('เกิดข้อผิดพลาด')
    }
  }

  // ============================================
  // Bulk toggle
  // ============================================
  const bulkToggleGames = async (status: GameStatus) => {
    if (selectedGames.length === 0) return
    try {
      await GameService.bulkToggleGames(selectedGames, status)
      setGames(prev => prev.map(g => 
        selectedGames.includes(g.id) ? { ...g, status } : g
      ))
      toast.success(`${status === 'ACTIVE' ? 'เปิด' : 'ปิด'}ใช้งาน ${selectedGames.length} เกมสำเร็จ`)
      setSelectedGames([])
    } catch (error) {
      toast.error('เกิดข้อผิดพลาด')
    }
  }

  // ============================================
  // Select all
  // ============================================
  const selectAllGames = () => {
    if (selectedGames.length === games.length) {
      setSelectedGames([])
    } else {
      setSelectedGames(games.map(g => g.id))
    }
  }

  const selectAllCategories = () => {
    if (selectedCategories.length === categories.length) {
      setSelectedCategories([])
    } else {
      setSelectedCategories(categories.map(c => c.id))
    }
  }

  const selectAllProviders = () => {
    if (selectedProviders.length === providers.length) {
      setSelectedProviders([])
    } else {
      setSelectedProviders(providers.map(p => p.id))
    }
  }

  // ============================================
  // Provider Status Badge
  // ============================================
  const getApiStatusBadge = (status?: string) => {
    const config: Record<string, { label: string; className: string; icon: React.ReactNode }> = {
      online: { label: 'Online', className: 'bg-emerald-100 text-emerald-700', icon: <Wifi className="h-3 w-3" /> },
      connected: { label: 'Online', className: 'bg-emerald-100 text-emerald-700', icon: <Wifi className="h-3 w-3" /> },
      offline: { label: 'Offline', className: 'bg-gray-100 text-gray-700', icon: <WifiOff className="h-3 w-3" /> },
      disconnected: { label: 'Offline', className: 'bg-gray-100 text-gray-700', icon: <WifiOff className="h-3 w-3" /> },
      maintenance: { label: 'Maintenance', className: 'bg-amber-100 text-amber-700', icon: <Wrench className="h-3 w-3" /> },
    }
    const cfg = config[status || 'connected'] || config.connected
    return (
      <Badge variant="secondary" className={cn("border-0 text-xs gap-1", cfg.className)}>
        {cfg.icon} {cfg.label}
      </Badge>
    )
  }

  const tabs = [
    { id: 'games' as TabType, label: 'เกม', icon: <Gamepad2 className="h-4 w-4" />, count: stats.games.active, total: gamesTotal },
    { id: 'categories' as TabType, label: 'ประเภท', icon: <Layers className="h-4 w-4" />, count: stats.categories.active, total: stats.categories.total },
    { id: 'providers' as TabType, label: 'ผู้ให้บริการ', icon: <Server className="h-4 w-4" />, count: stats.providers.active, total: stats.providers.total },
  ]

  const currentSelected = activeTab === 'games' ? selectedGames : activeTab === 'categories' ? selectedCategories : selectedProviders
  const totalPages = Math.ceil(gamesTotal / pageSize)

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">จัดการเกม</h1>
          <p className="text-muted-foreground">เปิด/ปิด เกม ประเภท และผู้ให้บริการ</p>
        </div>
        <div className="flex items-center gap-2">
          <Button 
            variant="default"
            onClick={handleSyncGames}
            disabled={isSyncing}
            className="gap-2"
          >
            {isSyncing ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" />
                กำลัง Sync...
              </>
            ) : (
              <>
                <Download className="h-4 w-4" />
                Sync เกม
              </>
            )}
          </Button>
          <Button 
            variant="outline" 
            size="icon"
            onClick={handleRefresh}
            disabled={isRefreshing}
          >
            <RefreshCcw className={cn("h-4 w-4", isRefreshing && "animate-spin")} />
          </Button>
        </div>
      </div>

      {/* Sync Result Dialog */}
      <Dialog open={showSyncDialog} onOpenChange={setShowSyncDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {isSyncing ? (
                <>
                  <Loader2 className="h-5 w-5 animate-spin text-primary" />
                  กำลัง Sync เกม...
                </>
              ) : syncResult?.success ? (
                <>
                  <CheckCircle className="h-5 w-5 text-green-500" />
                  Sync สำเร็จ!
                </>
              ) : (
                <>
                  <XCircle className="h-5 w-5 text-red-500" />
                  Sync ล้มเหลว
                </>
              )}
            </DialogTitle>
            <DialogDescription>
              {isSyncing ? 'กรุณารอสักครู่...' : syncResult?.message}
            </DialogDescription>
          </DialogHeader>
          
          {syncResult?.data && (
            <div className="space-y-3 py-4">
              <div className="grid grid-cols-2 gap-3">
                <div className="p-3 bg-muted/50 rounded-lg text-center">
                  <p className="text-2xl font-bold">{syncResult.data.totalProviders || 0}</p>
                  <p className="text-xs text-muted-foreground">Providers</p>
                </div>
                <div className="p-3 bg-muted/50 rounded-lg text-center">
                  <p className="text-2xl font-bold">{syncResult.data.totalGames || 0}</p>
                  <p className="text-xs text-muted-foreground">เกม</p>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="p-3 bg-green-50 dark:bg-green-900/20 rounded-lg text-center">
                  <p className="text-xl font-bold text-green-600">+{syncResult.data.gamesAdded || 0}</p>
                  <p className="text-xs text-muted-foreground">เกมใหม่</p>
                </div>
                <div className="p-3 bg-red-50 dark:bg-red-900/20 rounded-lg text-center">
                  <p className="text-xl font-bold text-red-600">-{syncResult.data.gamesRemoved || 0}</p>
                  <p className="text-xs text-muted-foreground">เกมที่ลบ</p>
                </div>
              </div>
              {syncResult.data.duration && (
                <p className="text-xs text-muted-foreground text-center">
                  ใช้เวลา {(syncResult.data.duration / 1000).toFixed(2)} วินาที
                </p>
              )}
            </div>
          )}

          {!isSyncing && (
            <div className="flex justify-end">
              <Button onClick={() => setShowSyncDialog(false)}>ปิด</Button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Quick Stats - Tab Cards */}
      <div className="grid gap-4 md:grid-cols-3">
        {tabs.map((tab) => (
          <Card 
            key={tab.id} 
            className={cn(
              "cursor-pointer transition-all hover:shadow-md border-2",
              activeTab === tab.id ? "border-primary shadow-md" : "border-transparent hover:border-primary/30"
            )}
            onClick={() => {
              setActiveTab(tab.id)
              setSearchTerm('')
              setSelectedGames([])
              setSelectedCategories([])
              setSelectedProviders([])
            }}
          >
            <CardContent className="p-5">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <div className={cn(
                    "p-3 rounded-xl transition-colors",
                    activeTab === tab.id 
                      ? "bg-primary text-primary-foreground shadow-lg shadow-primary/25" 
                      : "bg-muted"
                  )}>
                    {tab.icon}
                  </div>
                  <div>
                    <p className="font-semibold text-lg">{tab.label}</p>
                    <p className="text-sm text-muted-foreground">
                      {tab.count} / {tab.total} เปิดใช้งาน
                    </p>
                  </div>
                </div>
                <div className="text-right">
                  <p className={cn(
                    "text-3xl font-bold",
                    activeTab === tab.id ? "text-primary" : "text-foreground"
                  )}>
                    {tab.count}
                  </p>
                </div>
              </div>
              {/* Progress bar */}
              <div className="mt-4 h-1.5 bg-muted rounded-full overflow-hidden">
                <div 
                  className={cn(
                    "h-full rounded-full transition-all",
                    activeTab === tab.id ? "bg-primary" : "bg-muted-foreground/30"
                  )}
                  style={{ width: `${tab.total > 0 ? (tab.count / tab.total) * 100 : 0}%` }}
                />
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Main Content */}
      <Card>
        <CardHeader className="pb-4 border-b">
          <div className="flex flex-col lg:flex-row lg:items-center gap-4">
            {/* Search */}
            <div className="relative flex-1 max-w-sm">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder={`ค้นหา${activeTab === 'games' ? 'เกม' : activeTab === 'categories' ? 'ประเภท' : 'ผู้ให้บริการ'}...`}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-9"
              />
            </div>

            {/* Filters (only for games) */}
            {activeTab === 'games' && (
              <div className="flex flex-wrap items-center gap-2">
                <Select value={filterCategory} onValueChange={setFilterCategory}>
                  <SelectTrigger className="w-[140px]">
                    <Layers className="h-4 w-4 mr-2 text-muted-foreground" />
                    <SelectValue placeholder="ประเภท" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">ทุกประเภท</SelectItem>
                    {categories.map(cat => (
                      <SelectItem key={cat.id} value={cat.id}>
                        {cat.nameTh || cat.name} ({cat.gamesCount || 0})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>

                <Select value={filterProvider} onValueChange={setFilterProvider}>
                  <SelectTrigger className="w-[160px]">
                    <Server className="h-4 w-4 mr-2 text-muted-foreground" />
                    <SelectValue placeholder="ผู้ให้บริการ" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">ทุกผู้ให้บริการ</SelectItem>
                    {providers.map(prov => (
                      <SelectItem key={prov.id} value={prov.code}>
                        {prov.name} ({prov.gamesCount || 0})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>

                <Select value={filterStatus} onValueChange={(v) => setFilterStatus(v as any)}>
                  <SelectTrigger className="w-[130px]">
                    <SelectValue placeholder="สถานะ" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">ทุกสถานะ</SelectItem>
                    <SelectItem value="ACTIVE">เปิดใช้งาน</SelectItem>
                    <SelectItem value="INACTIVE">ปิดใช้งาน</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}

            {/* Bulk Actions */}
            {currentSelected.length > 0 && (
              <div className="flex items-center gap-2 ml-auto">
                <Badge variant="secondary" className="px-3 py-1">
                  เลือก {currentSelected.length} รายการ
                </Badge>
                <Button 
                  size="sm" 
                  variant="outline" 
                  className="gap-1.5 text-emerald-600 border-emerald-200 hover:bg-emerald-50"
                  onClick={() => {
                    if (activeTab === 'games') bulkToggleGames('ACTIVE')
                  }}
                >
                  <Power className="h-3.5 w-3.5" /> เปิด
                </Button>
                <Button 
                  size="sm" 
                  variant="outline" 
                  className="gap-1.5 text-red-600 border-red-200 hover:bg-red-50"
                  onClick={() => {
                    if (activeTab === 'games') bulkToggleGames('INACTIVE')
                  }}
                >
                  <PowerOff className="h-3.5 w-3.5" /> ปิด
                </Button>
              </div>
            )}
          </div>
        </CardHeader>

        <CardContent className="p-0">
          {isLoading ? (
            <TableSkeleton rows={8} />
          ) : (
            <>
              {/* Games Table */}
              {activeTab === 'games' && (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-muted/50">
                      <tr>
                        <th className="px-4 py-3 text-left w-10">
                          <Checkbox 
                            checked={selectedGames.length === games.length && games.length > 0}
                            onCheckedChange={selectAllGames}
                          />
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">เกม</th>
                        <th className="px-4 py-3 text-center text-xs font-medium text-muted-foreground uppercase tracking-wider">ประเภท</th>
                        <th className="px-4 py-3 text-center text-xs font-medium text-muted-foreground uppercase tracking-wider">ผู้ให้บริการ</th>
                        <th className="px-4 py-3 text-center text-xs font-medium text-muted-foreground uppercase tracking-wider w-20">สถานะ</th>
                        <th className="px-4 py-3 text-center text-xs font-medium text-muted-foreground uppercase tracking-wider w-16">ดู</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y">
                      {games.map((game) => (
                        <tr key={game.id} className={cn(
                          "hover:bg-muted/30 transition-colors",
                          game.status === 'INACTIVE' && "opacity-60"
                        )}>
                          <td className="px-4 py-3">
                            <Checkbox 
                              checked={selectedGames.includes(game.id)}
                              onCheckedChange={(checked) => {
                                if (checked) setSelectedGames([...selectedGames, game.id])
                                else setSelectedGames(selectedGames.filter(id => id !== game.id))
                              }}
                            />
                          </td>
                          <td className="px-4 py-3">
                            <div className="flex items-center gap-3">
                              <div className="h-12 w-12 rounded-lg bg-gradient-to-br from-primary/10 to-primary/5 flex items-center justify-center overflow-hidden border">
                                {game.image_vertical ? (
                                  <img 
                                    src={game.image_vertical} 
                                    alt={game.gameName}
                                    className="h-full w-full object-cover"
                                    onError={(e) => {
                                      (e.target as HTMLImageElement).style.display = 'none'
                                    }}
                                  />
                                ) : (
                                  <ImageIcon className="h-5 w-5 text-muted-foreground" />
                                )}
                              </div>
                              <div>
                                <div className="flex items-center gap-2">
                                  <p className="font-medium">{game.gameName}</p>
                                  {game.isHot && (
                                    <Badge className="text-[10px] bg-orange-500 text-white border-0 h-4 px-1.5">
                                      <Flame className="h-2.5 w-2.5 mr-0.5" />HOT
                                    </Badge>
                                  )}
                                  {game.isNew && (
                                    <Badge className="text-[10px] bg-blue-500 text-white border-0 h-4 px-1.5">
                                      <Sparkles className="h-2.5 w-2.5 mr-0.5" />NEW
                                    </Badge>
                                  )}
                                </div>
                                {game.gameNameTh && game.gameNameTh !== game.gameName && (
                                  <p className="text-xs text-muted-foreground">{game.gameNameTh}</p>
                                )}
                              </div>
                            </div>
                          </td>
                          <td className="px-4 py-3 text-center">
                            <Badge variant="outline" className="font-normal">
                              {game.game_Category || '-'}
                            </Badge>
                          </td>
                          <td className="px-4 py-3 text-center">
                            <Badge variant="secondary" className="bg-muted font-normal">
                              {game.provider || '-'}
                            </Badge>
                          </td>
                          <td className="px-4 py-3 text-center">
                            <Switch 
                              checked={game.status === 'ACTIVE'}
                              onCheckedChange={() => toggleGame(game.id, game.status)}
                            />
                          </td>
                          <td className="px-4 py-3 text-center">
                            <Button 
                              size="icon" 
                              variant="ghost" 
                              className="h-8 w-8"
                              onClick={() => {
                                setSelectedGame(game)
                                setShowDetail(true)
                              }}
                            >
                              <Eye className="h-4 w-4" />
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  
                  {games.length === 0 && (
                    <div className="text-center py-16">
                      <Gamepad2 className="h-12 w-12 mx-auto text-muted-foreground/50 mb-3" />
                      <p className="text-muted-foreground">ไม่พบเกมที่ค้นหา</p>
                    </div>
                  )}
                  
                  {/* Pagination */}
                  {games.length > 0 && (
                    <div className="flex items-center justify-between px-4 py-3 border-t bg-muted/30">
                      <div className="text-sm text-muted-foreground">
                        แสดง {((page - 1) * pageSize) + 1} - {Math.min(page * pageSize, gamesTotal)} จาก {gamesTotal} รายการ
                      </div>
                      <div className="flex items-center gap-2">
                        <Select value={String(pageSize)} onValueChange={(v) => {
                          setPageSize(Number(v))
                          setPage(1)
                        }}>
                          <SelectTrigger className="w-[80px] h-8">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="10">10</SelectItem>
                            <SelectItem value="20">20</SelectItem>
                            <SelectItem value="50">50</SelectItem>
                            <SelectItem value="100">100</SelectItem>
                          </SelectContent>
                        </Select>
                        <Button 
                          size="icon" 
                          variant="outline" 
                          className="h-8 w-8"
                          disabled={page <= 1}
                          onClick={() => setPage(page - 1)}
                        >
                          <ChevronLeft className="h-4 w-4" />
                        </Button>
                        <span className="text-sm px-2">
                          {page} / {totalPages || 1}
                        </span>
                        <Button 
                          size="icon" 
                          variant="outline" 
                          className="h-8 w-8"
                          disabled={page >= totalPages}
                          onClick={() => setPage(page + 1)}
                        >
                          <ChevronRight className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Categories Table */}
              {activeTab === 'categories' && (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-muted/50">
                      <tr>
                        <th className="px-4 py-3 text-left w-10">
                          <Checkbox 
                            checked={selectedCategories.length === categories.length && categories.length > 0}
                            onCheckedChange={selectAllCategories}
                          />
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">ประเภท</th>
                        <th className="px-4 py-3 text-center text-xs font-medium text-muted-foreground uppercase tracking-wider">จำนวนเกม</th>
                        <th className="px-4 py-3 text-center text-xs font-medium text-muted-foreground uppercase tracking-wider">เปิดใช้งาน</th>
                        <th className="px-4 py-3 text-center text-xs font-medium text-muted-foreground uppercase tracking-wider w-24">สถานะ</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y">
                      {categories.map((category) => (
                        <tr key={category.id} className={cn(
                          "hover:bg-muted/30 transition-colors",
                          category.status === 'INACTIVE' && "opacity-60"
                        )}>
                          <td className="px-4 py-3">
                            <Checkbox 
                              checked={selectedCategories.includes(category.id)}
                              onCheckedChange={(checked) => {
                                if (checked) setSelectedCategories([...selectedCategories, category.id])
                                else setSelectedCategories(selectedCategories.filter(id => id !== category.id))
                              }}
                            />
                          </td>
                          <td className="px-4 py-3">
                            <div className="flex items-center gap-3">
                              <div className="h-12 w-12 rounded-xl bg-gradient-to-br from-primary/20 to-primary/5 flex items-center justify-center text-2xl border">
                                {category.icon || <Layers className="h-6 w-6 text-primary" />}
                              </div>
                              <div>
                                <p className="font-medium">{category.nameTh || category.name}</p>
                                {category.nameTh && category.name !== category.nameTh && (
                                  <p className="text-xs text-muted-foreground">{category.name}</p>
                                )}
                              </div>
                            </div>
                          </td>
                          <td className="px-4 py-3 text-center">
                            <span className="text-xl font-bold">{category.gamesCount || 0}</span>
                            <span className="text-muted-foreground ml-1">เกม</span>
                          </td>
                          <td className="px-4 py-3 text-center">
                            <Badge variant="secondary" className={cn(
                              "border-0",
                              (category.activeGames || 0) === (category.gamesCount || 0) 
                                ? "bg-emerald-100 text-emerald-700" 
                                : "bg-amber-100 text-amber-700"
                            )}>
                              {category.activeGames || 0}/{category.gamesCount || 0}
                            </Badge>
                          </td>
                          <td className="px-4 py-3 text-center">
                            <Switch 
                              checked={category.status === 'ACTIVE'}
                              onCheckedChange={() => toggleCategory(category.id, category.status)}
                            />
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  
                  {categories.length === 0 && (
                    <div className="text-center py-16">
                      <Layers className="h-12 w-12 mx-auto text-muted-foreground/50 mb-3" />
                      <p className="text-muted-foreground">ไม่พบประเภท</p>
                    </div>
                  )}
                </div>
              )}

              {/* Providers Table */}
              {activeTab === 'providers' && (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-muted/50">
                      <tr>
                        <th className="px-4 py-3 text-left w-10">
                          <Checkbox 
                            checked={selectedProviders.length === providers.length && providers.length > 0}
                            onCheckedChange={selectAllProviders}
                          />
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">ผู้ให้บริการ</th>
                        <th className="px-4 py-3 text-center text-xs font-medium text-muted-foreground uppercase tracking-wider">จำนวนเกม</th>
                        <th className="px-4 py-3 text-center text-xs font-medium text-muted-foreground uppercase tracking-wider">เปิดใช้งาน</th>
                        <th className="px-4 py-3 text-center text-xs font-medium text-muted-foreground uppercase tracking-wider">API Status</th>
                        <th className="px-4 py-3 text-center text-xs font-medium text-muted-foreground uppercase tracking-wider w-24">สถานะ</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y">
                      {providers.map((provider) => (
                        <tr key={provider.id} className={cn(
                          "hover:bg-muted/30 transition-colors",
                          provider.status === 'INACTIVE' && "opacity-60"
                        )}>
                          <td className="px-4 py-3">
                            <Checkbox 
                              checked={selectedProviders.includes(provider.id)}
                              onCheckedChange={(checked) => {
                                if (checked) setSelectedProviders([...selectedProviders, provider.id])
                                else setSelectedProviders(selectedProviders.filter(id => id !== provider.id))
                              }}
                            />
                          </td>
                          <td className="px-4 py-3">
                            <div className="flex items-center gap-3">
                              <div className="h-12 w-12 rounded-xl bg-gradient-to-br from-violet-100 to-violet-50 flex items-center justify-center overflow-hidden border">
                                {provider.logo ? (
                                  <img 
                                    src={provider.logo} 
                                    alt={provider.name}
                                    className="h-8 w-8 object-contain"
                                  />
                                ) : (
                                  <Server className="h-6 w-6 text-violet-500" />
                                )}
                              </div>
                              <div>
                                <p className="font-medium">{provider.name}</p>
                                <p className="text-xs text-muted-foreground font-mono">{provider.code}</p>
                              </div>
                            </div>
                          </td>
                          <td className="px-4 py-3 text-center">
                            <span className="text-xl font-bold">{provider.gamesCount || 0}</span>
                            <span className="text-muted-foreground ml-1">เกม</span>
                          </td>
                          <td className="px-4 py-3 text-center">
                            <Badge variant="secondary" className={cn(
                              "border-0",
                              (provider.activeGames || 0) === (provider.gamesCount || 0) 
                                ? "bg-emerald-100 text-emerald-700" 
                                : "bg-amber-100 text-amber-700"
                            )}>
                              {provider.activeGames || 0}/{provider.gamesCount || 0}
                            </Badge>
                          </td>
                          <td className="px-4 py-3 text-center">
                            {getApiStatusBadge(provider.apiStatus)}
                          </td>
                          <td className="px-4 py-3 text-center">
                            <Switch 
                              checked={provider.status === 'ACTIVE'}
                              onCheckedChange={() => toggleProvider(provider.id, provider.status)}
                            />
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  
                  {providers.length === 0 && (
                    <div className="text-center py-16">
                      <Server className="h-12 w-12 mx-auto text-muted-foreground/50 mb-3" />
                      <p className="text-muted-foreground">ไม่พบผู้ให้บริการ</p>
                    </div>
                  )}
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>

      {/* Footer Legend */}
      <div className="flex items-center justify-between p-4 rounded-lg bg-muted/30 border">
        <div className="flex items-center gap-6 text-sm">
          <div className="flex items-center gap-2">
            <div className="h-3 w-3 rounded-full bg-emerald-500" />
            <span>เปิดใช้งาน</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="h-3 w-3 rounded-full bg-gray-300" />
            <span>ปิดใช้งาน</span>
          </div>
        </div>
        <div className="text-sm text-muted-foreground">
          {activeTab === 'games' && `${stats.games.active}/${gamesTotal} เกม`}
          {activeTab === 'categories' && `${stats.categories.active}/${stats.categories.total} ประเภท`}
          {activeTab === 'providers' && `${stats.providers.active}/${stats.providers.total} ผู้ให้บริการ`}
        </div>
      </div>
      
      {/* Game Detail Dialog */}
      <GameDetailDialog 
        game={selectedGame}
        open={showDetail}
        onOpenChange={setShowDetail}
      />

      {/* Sync Confirm Dialog */}
      <ConfirmDialog
        open={showSyncConfirm}
        onOpenChange={setShowSyncConfirm}
        title="ยืนยันการ Sync เกม"
        description="คุณต้องการดึงข้อมูลเกมทั้งหมดจาก API หรือไม่?"
        variant="warning"
        confirmLabel="Sync เลย"
        details={
          <div className="space-y-1 text-sm">
            <div className="flex items-center gap-2">
              <Download className="w-4 h-4 text-blue-500" />
              <span>ดึงข้อมูลเกมและ Provider จาก API</span>
            </div>
            <div className="flex items-center gap-2">
              <Loader2 className="w-4 h-4 text-amber-500" />
              <span>อาจใช้เวลา 1-2 นาที</span>
            </div>
            <div className="flex items-center gap-2">
              <RefreshCcw className="w-4 h-4 text-emerald-500" />
              <span>ข้อมูลเกมจะถูกอัพเดทใหม่ทั้งหมด</span>
            </div>
          </div>
        }
        onConfirm={executeSyncGames}
      />
    </div>
  )
}
